import { createSHA256 } from 'hash-wasm'

// The algorithm label recorded on the document as `fileChecksumAlgorithm`.
// Keep this in one place so the digest and its label can never drift apart.
export const FILE_CHECKSUM_ALGORITHM = 'SHA-256'

const CHUNK_SIZE = 8 * 1024 * 1024 // 8 MB — stream the file so large uploads
// don't have to be read fully into memory at once.

// The slice of hash-wasm's `IHasher` we actually depend on. Declaring it here
// (rather than importing the concrete type) lets `makeComputeFileChecksum` take
// a fake hasher in tests without pulling in the WASM module.
export interface IFileChecksumHasher {
  init(): unknown
  update(data: Uint8Array): unknown
  digest(outputType: 'hex'): string
}

export interface IComputeFileChecksumOptions {
  // Streaming chunk size in bytes. Overridable so tests can exercise the
  // multi-chunk path without allocating multi-megabyte buffers.
  chunkSize?: number
}

// Factory that injects the hasher (and optional chunk size) so the streaming
// digest logic can be unit-tested against a fake hasher. The returned function
// re-`init()`s the hasher on every call, so a single hasher can be reused
// across files.
export function makeComputeFileChecksum(
  hasher: IFileChecksumHasher,
  { chunkSize = CHUNK_SIZE }: IComputeFileChecksumOptions = {}
) {
  return async function computeFileChecksum(file: File): Promise<string> {
    hasher.init()
    for (let offset = 0; offset < file.size; offset += chunkSize) {
      const chunk = file.slice(offset, offset + chunkSize)
      hasher.update(new Uint8Array(await chunk.arrayBuffer()))
    }
    return hasher.digest('hex') // 64-char lowercase hex
  }
}

export async function computeFileChecksum(file: File): Promise<string> {
  const hasher = await createSHA256()
  return makeComputeFileChecksum(hasher)(file)
}
